import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useBusinessContext } from '@context'
import { useHistory } from 'react-router-dom'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@hooks'
import { fetchAIDocumentCreateBatchStatus, AIDocumentCreateStatus } from '@query'
import { getDocumentsPageUrl } from '@constants'
import toast from 'react-hot-toast'
import styled from 'styled-components'

const TERMINAL_STATUSES: AIDocumentCreateStatus[] = ['COMPLETED', 'FAILED', 'CANCELLED']
const IN_PROGRESS_STATUSES: AIDocumentCreateStatus[] = ['PENDING', 'IN_PROGRESS']
const TOAST_ID = 'ai-document-create-batch'

type BatchJobStats = {
  completed: number
  failed: number
  inProgress: number
  total: number
  allTerminal: boolean
}

function getBatchJobStats(
  jobs: { status: AIDocumentCreateStatus }[],
  total: number
): BatchJobStats {
  const completed = jobs.filter(job => job.status === 'COMPLETED').length
  const failed = jobs.filter(job =>
    (['FAILED', 'CANCELLED'] as AIDocumentCreateStatus[]).includes(job.status)
  ).length
  const inProgress = jobs.filter(job => IN_PROGRESS_STATUSES.includes(job.status)).length
  const allTerminal = jobs.every(job => TERMINAL_STATUSES.includes(job.status))
  return { completed, failed, inProgress, total, allTerminal }
}

function showProgressToast(stats: BatchJobStats, translate: TFunction, toastStyle: object) {
  const { completed, failed, inProgress, total } = stats
  toast.loading(
    <ProgressToastContent>
      <p>{translate('document.createWithAI.status.creatingMultiple', { total })}</p>
      <p>
        {failed > 0
          ? translate('document.createWithAI.status.progressWithFailures', {
              completed,
              inProgress,
              failed
            })
          : translate('document.createWithAI.status.progress', { completed, inProgress })}
      </p>
      <p>
        {total > 1
          ? translate('document.createWithAI.status.backgroundHintMultiple')
          : translate('document.createWithAI.status.backgroundHint')}
      </p>
    </ProgressToastContent>,
    { id: TOAST_ID, style: toastStyle }
  )
}

function showTerminalToasts(stats: BatchJobStats, translate: TFunction, toastStyle: object) {
  const { completed, failed, total } = stats
  toast.dismiss(TOAST_ID)
  if (failed === total) {
    toast.error(translate('document.createWithAI.error.analysis_failed'), {
      id: TOAST_ID,
      style: toastStyle
    })
  } else if (failed > 0) {
    toast.success(
      translate('document.createWithAI.status.batchPartialFailure', { completed, failed }),
      {
        id: TOAST_ID,
        style: toastStyle
      }
    )
  } else {
    toast.success(translate('document.createWithAI.status.batchCompleted', { count: total }), {
      id: TOAST_ID,
      style: toastStyle
    })
  }
}

export const useAIDocumentCreateTracker = () => {
  const { slug } = useBusinessContext()
  const history = useHistory()
  const queryClient = useQueryClient()
  const [translate] = useTranslation()
  const theme = useTheme()
  const [activeJobIds, setActiveJobIds] = useState<string[]>([])
  const lastCompletedCountRef = useRef<number>(0)

  const toastStyle = useMemo(
    () => ({
      borderRadius: `${theme.borderRadius.md}rem`,
      background: theme.colors.menuBg,
      color: theme.colors.neutralWhite
    }),
    [theme.borderRadius.md, theme.colors.menuBg, theme.colors.neutralWhite]
  )

  const startTracking = (payload: { jobIds: string[] }) => {
    if (payload.jobIds.length === 0) return
    setActiveJobIds(payload.jobIds)
    lastCompletedCountRef.current = 0
    const total = payload.jobIds.length
    toast.loading(
      <ProgressToastContent>
        <p>{translate('document.createWithAI.status.creatingMultiple', { total })}</p>
        <p>
          {translate('document.createWithAI.status.progress', { completed: 0, inProgress: total })}
        </p>
        <p>
          {total > 1
            ? translate('document.createWithAI.status.backgroundHintMultiple')
            : translate('document.createWithAI.status.backgroundHint')}
        </p>
      </ProgressToastContent>,
      { id: TOAST_ID, style: toastStyle }
    )
  }

  const { data: batchData, error: batchError } = useQuery({
    queryKey: [slug, 'ai-document-create-batch-status', activeJobIds],
    queryFn: () => fetchAIDocumentCreateBatchStatus({ slug, jobIds: activeJobIds }),
    enabled: activeJobIds.length > 0,
    refetchInterval: query => {
      const jobs = query.state.data?.jobs ?? []
      if (jobs.length === 0) return 2000
      const allTerminal = jobs.every(job => TERMINAL_STATUSES.includes(job.status))
      return allTerminal ? false : 2000
    }
  })

  useEffect(() => {
    if (!batchData?.jobs?.length || activeJobIds.length === 0) return

    const jobs = batchData.jobs
    const total = activeJobIds.length
    const stats = getBatchJobStats(jobs, total)

    if (stats.completed !== lastCompletedCountRef.current || stats.allTerminal) {
      lastCompletedCountRef.current = stats.completed
      showProgressToast(stats, translate, toastStyle)
    }

    if (!stats.allTerminal) return

    showTerminalToasts(stats, translate, toastStyle)
    void queryClient.invalidateQueries({ queryKey: [slug, 'documents'] })

    if (total === 1) {
      const single = jobs.find(job => job.status === 'COMPLETED') as
        | { document_id?: number }
        | undefined
      if (single?.document_id) {
        history.push(getDocumentsPageUrl(slug, { id: single.document_id }))
      }
    }

    setActiveJobIds([])
  }, [activeJobIds.length, batchData, history, queryClient, slug, translate, toastStyle])

  useEffect(() => {
    if (!batchError || activeJobIds.length === 0) return
    const payload = (batchError as { data?: { error_message?: string } })?.data
    toast.error(
      payload?.error_message || translate('document.createWithAI.error.analysis_failed'),
      {
        id: TOAST_ID,
        style: toastStyle
      }
    )
    setActiveJobIds([])
  }, [activeJobIds.length, batchError, translate, toastStyle])

  useEffect(() => {
    if (activeJobIds.length > 0) return
    lastCompletedCountRef.current = 0
  }, [activeJobIds.length])

  return { startTracking }
}

const ProgressToastContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;

  & > p {
    margin: 0;
  }
`
